import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View, type ViewInstance } from 'react-native';

import { IconButton, Portal } from '@/components/ui';

export interface ExportMenuProps {
	/** Run when "Export to Excel" is selected. */
	onExcel: () => void;
	/** Run when "Export to CSV" is selected. */
	onCsv: () => void;
	/** Disables the trigger button (no rows loaded yet, etc.). */
	disabled?: boolean;
}

/**
 * Trigger icon button (download-outline) → opens a small anchored menu with
 * "Excel (.xlsx)" and "CSV" options. Anchored via getBoundingClientRect on
 * web and measureInWindow on native — same Portal+fixed pattern as the
 * grid typeahead so the menu doesn't get clipped by parent containers.
 */
export const ExportMenu: React.FC<ExportMenuProps> = ({ onExcel, onCsv, disabled }) => {
	const wrapRef = useRef<ViewInstance>(null);
	const [open, setOpen] = useState(false);
	const [anchorRect, setAnchorRect] = useState<{ top: number; right: number } | null>(null);

	const measureAnchor = useCallback(() => {
		const node = wrapRef.current as unknown as {
			getBoundingClientRect?: () => DOMRect;
			measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
		} | null;
		if (!node) return;
		if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
			const r = node.getBoundingClientRect();
			const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : r.right;
			setAnchorRect({ top: r.bottom + 4, right: viewportWidth - r.right });
		} else if (typeof node.measureInWindow === 'function') {
			node.measureInWindow((x, y, w, h) => {
				// On native we don't have window width; approximate with screen.
				setAnchorRect({ top: y + h + 4, right: x });
			});
		}
	}, []);

	useEffect(() => {
		if (!open) return;
		measureAnchor();
		if (Platform.OS !== 'web') return;
		const onMove = () => measureAnchor();
		window.addEventListener('scroll', onMove, true);
		window.addEventListener('resize', onMove);
		return () => {
			window.removeEventListener('scroll', onMove, true);
			window.removeEventListener('resize', onMove);
		};
	}, [open, measureAnchor]);

	const choose = (handler: () => void) => () => {
		setOpen(false);
		handler();
	};

	return (
		<View ref={wrapRef}>
			<IconButton
				icon="download-outline"
				size={22}
				onPress={() => setOpen((o) => !o)}
				disabled={disabled}
				accessibilityLabel="Export"
				testID="report-action-export"
			/>
			{open && anchorRect && (
				<Portal>
					{/* Full-screen pressable backdrop closes the menu when tapped. */}
					<Pressable
						onPress={() => setOpen(false)}
						style={[
							styles.backdrop,
							{ position: Platform.OS === 'web' ? ('fixed' as 'absolute') : 'absolute' },
						]}
					/>
					<View
						style={[
							styles.anchor,
							{
								position: Platform.OS === 'web' ? ('fixed' as 'absolute') : 'absolute',
								top: anchorRect.top,
								right: anchorRect.right,
							},
						]}
					>
						<View style={styles.menu}>
							<MenuItem icon="grid-outline" label="Excel (.xlsx)" onPress={choose(onExcel)} testID="export-menu-excel" />
							<MenuItem icon="document-text-outline" label="CSV" onPress={choose(onCsv)} testID="export-menu-csv" />
						</View>
					</View>
				</Portal>
			)}
		</View>
	);
};

const MenuItem: React.FC<{ icon: string; label: string; onPress: () => void; testID?: string }> = ({
	icon,
	label,
	onPress,
	testID,
}) => (
	<Pressable onPress={onPress} style={({ pressed }) => [styles.item, pressed && styles.itemPressed]} testID={testID}>
		<Ionicons name={icon as 'grid-outline'} size={16} color="#37474f" style={{ marginRight: 8 }} />
		<Text style={styles.itemLabel}>{label}</Text>
	</Pressable>
);

const styles = StyleSheet.create({
	backdrop: {
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 99998,
	},
	anchor: {
		zIndex: 99999,
	},
	menu: {
		backgroundColor: '#fff',
		borderWidth: 1,
		borderColor: '#cfd8dc',
		borderRadius: 6,
		minWidth: 180,
		shadowColor: '#000',
		shadowOpacity: 0.15,
		shadowRadius: 12,
		shadowOffset: { width: 0, height: 6 },
		elevation: 24,
		overflow: 'hidden',
	},
	item: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 10,
		paddingHorizontal: 12,
	},
	itemPressed: {
		backgroundColor: '#eceff1',
	},
	itemLabel: {
		fontSize: 13,
		color: '#212121',
	},
});
